import { useCallback, useEffect, useState } from 'react';
import { useApi } from '@mainstage/core-plugin-api';
import { customAlertApiRef } from '@agilelab/plugin-wb-platform';
import { panelCatalogApiRef } from '../api';
import { useApprovalStatus, ApprovalStatus } from './useApprovalStatus';

const NO_ACTIVE_WORKFLOW_STATUS: ApprovalStatus = {
  exists: false,
  status: 'NO_ACTIVE_WORKFLOW',
  currentStage: null,
  terminal: false,
  context: {},
};

/**
 * Polls the approval status endpoint every 2 s until the workflow exists,
 * or gives up after ~30 s. Returns the final ApprovalStatus if found,
 * or `null` if the polling timed out.
 */
async function waitUntilWorkflowExists(
  fetchStatus: () => Promise<ApprovalStatus>,
): Promise<ApprovalStatus | null> {
  const POLL_INTERVAL_MS = 2000;
  const MAX_ATTEMPTS = 15;
  for (let i = 0; i < MAX_ATTEMPTS; i++) {
    await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
    try {
      const status = await fetchStatus();
      if (status.exists) return status;
    } catch {
      // ignore transient fetch errors, keep polling
    }
  }
  return null;
}

export interface UseDeployRequestOptions {
  releaseEntityName: string;
  /**
   * The environment to check approval status for.
   * When `undefined` (e.g. the env has not been selected yet),
   * polling is skipped and the hook immediately reports NO_ACTIVE_WORKFLOW.
   */
  environment: string | undefined;
}

export interface UseDeployRequestResult {
  /** Effective approval status (includes optimistic override). Null while initial fetch is in flight. */
  approvalStatus: ApprovalStatus | null;
  /** True until the first status fetch completes. */
  loading: boolean;
  /** Whether an approval workflow definition is active for this entity (i.e. status !== NO_ACTIVE_WORKFLOW). */
  isApprovalActive: boolean;
  /** Whether to show "Request Deploy" (workflow not started yet, or rejected). */
  showRequestDeploy: boolean;
  /** Whether the workflow is pending approval (running, not yet terminal). */
  showWaitingForApproval: boolean;
  /** The approver role extracted from workflow context (e.g. "data-product-owner"). */
  approverRole: string | undefined;
  /** Computed label for the deploy action button. */
  deployButtonLabel: 'Request Deploy' | 'Waiting for Approval' | 'Deploy';
  /** True while a deploy request is in flight. */
  isRequestingDeploy: boolean;
  /** Submits a deploy request, shows optimistic "Waiting" state, then polls for workflow creation. */
  handleRequestDeploy: () => Promise<void>;
  /** Force a re-fetch of the approval status. */
  refetch: () => void;
}

/**
 * Shared hook for deploy approval state management.
 *
 * Encapsulates approval status polling, optimistic override, background
 * refetch, and the "Request Deploy" submission logic. Used by both
 * ProvisioningCardActions (Release Detail page) and DeployUndeployDialog
 * (Release List 3-dot menu) to ensure identical behaviour.
 */
export function useDeployRequest({
  releaseEntityName,
  environment,
}: UseDeployRequestOptions): UseDeployRequestResult {
  const panelCatalogApi = useApi(panelCatalogApiRef);
  const alertApi = useApi(customAlertApiRef);

  const [isRequestingDeploy, setIsRequestingDeploy] = useState(false);
  const [optimisticRunning, setOptimisticRunning] = useState(false);

  // Reset optimistic state whenever the environment changes so stale overrides
  // from a previous selection don't bleed into the new one.
  useEffect(() => {
    setOptimisticRunning(false);
  }, [environment]);

  const fetchDeployApprovalStatus = useCallback((): Promise<ApprovalStatus> => {
    if (!environment) return Promise.resolve(NO_ACTIVE_WORKFLOW_STATUS);
    return panelCatalogApi.getDeployRequestStatus(
      releaseEntityName,
      environment,
    );
  }, [panelCatalogApi, releaseEntityName, environment]);

  const {
    approvalStatus: polledStatus,
    loading: approvalLoading,
    refetch,
  } = useApprovalStatus(fetchDeployApprovalStatus);

  useEffect(() => {
    // Once the real polled status confirms the workflow exists, clear the
    // optimistic override.
    if (optimisticRunning && polledStatus?.exists) {
      setOptimisticRunning(false);
    }
  }, [optimisticRunning, polledStatus]);

  // Optimistic override: show RUNNING immediately after the user clicks
  // "Request Deploy" before the backend confirms the workflow was created.
  // Dropped as soon as the backend returns a real status with exists=true.
  const approvalStatus: ApprovalStatus | null =
    optimisticRunning && !polledStatus?.exists
      ? {
          exists: true,
          status: 'RUNNING',
          currentStage: null,
          terminal: false,
          context: {},
        }
      : polledStatus;

  const handleRequestDeploy = useCallback(async () => {
    if (!environment) return;
    setIsRequestingDeploy(true);
    try {
      await panelCatalogApi.requestDeploy(releaseEntityName, environment);
      setOptimisticRunning(true);
    } catch (error) {
      alertApi.post({ error, severity: 'error' });
      setIsRequestingDeploy(false);
      return;
    }

    const result = await waitUntilWorkflowExists(fetchDeployApprovalStatus);
    setIsRequestingDeploy(false);

    if (result?.status === 'FAILED') {
      alertApi.post({
        message: `Deploy request failed: ${
          result.context?.failureReason ?? 'Unknown error'
        }`,
        severity: 'error',
      });
      refetch();
    } else if (result?.status === 'APPROVED') {
      // The workflow was created and immediately resolved — auto-approve triggered.
      // This happens when the requester is also an authorized approver.
      alertApi.post({
        message:
          'Your deploy request was automatically approved because you are an authorized approver.',
        severity: 'success',
        timeoutMillis: 8000,
      });
      refetch();
    } else if (result) {
      alertApi.post({
        message: 'Deploy request sent. Approval pending.',
        severity: 'success',
        timeoutMillis: 6000,
      });
      refetch();
    } else {
      alertApi.post({
        message:
          'The deploy request is taking longer than expected to process. ' +
          'Please check back in a few moments or try again. ' +
          'If the issue persists, contact the platform team.',
        severity: 'warning',
      });
      // Reset optimistic state so the button returns to "Request Deploy",
      // letting the user retry manually. Background polling is intentionally
      // not started here — the toast already instructs the user to come back later.
      setOptimisticRunning(false);
    }
  }, [
    panelCatalogApi,
    releaseEntityName,
    environment,
    alertApi,
    fetchDeployApprovalStatus,
    refetch,
  ]);

  const isApprovalActive =
    approvalStatus !== null && approvalStatus.status !== 'NO_ACTIVE_WORKFLOW';
  const showRequestDeploy =
    isApprovalActive &&
    (approvalStatus.status === 'NO_WORKFLOW_INSTANCE' ||
      approvalStatus.status === 'REJECTED' ||
      approvalStatus.status === 'FAILED');
  const showWaitingForApproval =
    isApprovalActive && approvalStatus.exists && !approvalStatus.terminal;

  const deployButtonLabel = (():
    | 'Request Deploy'
    | 'Waiting for Approval'
    | 'Deploy' => {
    if (showRequestDeploy) return 'Request Deploy';
    if (showWaitingForApproval) return 'Waiting for Approval';
    return 'Deploy';
  })();

  const approverRole = approvalStatus?.context?.approver as string | undefined;

  return {
    approvalStatus,
    loading: approvalLoading,
    isApprovalActive,
    showRequestDeploy,
    showWaitingForApproval,
    approverRole,
    deployButtonLabel,
    isRequestingDeploy,
    handleRequestDeploy,
    refetch,
  };
}
